// Q3 "What you help them with" — pickone_editable single-select.
//
// Value shape: { options: string[], selectedIndex: number | null,
//                sourceKind: 'articles' | 'manual' }
//
// On mount: if the coach has published articles, seed options from their
// titles (sourceKind='articles'). If not, options start empty and the
// coach types their own scenarios (sourceKind='manual').
//
// UX matches the wireframe:
//   - Radio list of options (each row = radio + editable text)
//   - "+ Add another" button below the list
//   - "+ Write a new article or link to one" secondary link → /v2/articles/new
//
// The selected option's text feeds the campaign generator (replaces the
// legacy `promote` freetext question).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Box, Group, Radio, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { IconArticle, IconPlus, IconX } from '@tabler/icons-react';
import useApi from '../../hooks/useApi';
import { fetchArticles } from '../../services/api';

function nextValue(existing, patch) {
  return {
    options: Array.isArray(existing?.options) ? existing.options : [],
    selectedIndex: typeof existing?.selectedIndex === 'number' ? existing.selectedIndex : null,
    sourceKind: existing?.sourceKind || 'manual',
    ...patch,
  };
}

export default function HelpScreen({ question, value, onChange, hideHeader = false }) {
  const api = useApi();
  const navigate = useNavigate();
  const seededRef = useRef(false);
  const [drafting, setDrafting] = useState(false);

  const options = Array.isArray(value?.options) ? value.options : [];
  const selectedIndex = typeof value?.selectedIndex === 'number' ? value.selectedIndex : null;

  // Seed once from articles on first mount when options empty.
  useEffect(() => {
    if (seededRef.current) return;
    if (options.length > 0) { seededRef.current = true; return; }
    seededRef.current = true;
    fetchArticles(api)
      .then((articles) => {
        const titles = (articles || [])
          .map((a) => String(a?.title || '').trim())
          .filter(Boolean);
        if (!titles.length) return; // no articles yet — leave empty, coach will type
        onChange(nextValue(value, { options: titles, selectedIndex: 0, sourceKind: 'articles' }));
      })
      .catch(() => { /* ignore — coach can type their own */ });
  }, [api, options.length, onChange, value]);

  function setOptionText(index, text) {
    const next = options.slice();
    next[index] = text;
    onChange(nextValue(value, { options: next }));
  }

  function addOption(seed = '') {
    const next = [...options, seed];
    onChange(nextValue(value, {
      options: next,
      selectedIndex: selectedIndex == null ? next.length - 1 : selectedIndex,
      sourceKind: 'manual', // once the coach adds their own, treat the list as manual
    }));
    setDrafting(true);
  }

  function removeOption(index) {
    const next = options.filter((_, i) => i !== index);
    let nextSelected = selectedIndex;
    if (nextSelected === index) nextSelected = next.length ? 0 : null;
    else if (nextSelected != null && nextSelected > index) nextSelected -= 1;
    onChange(nextValue(value, { options: next, selectedIndex: nextSelected }));
  }

  function pick(index) {
    onChange(nextValue(value, { selectedIndex: index }));
  }

  const showEmpty = options.length === 0 && !drafting;

  return (
    <Stack gap="lg">
      {!hideHeader && (
        <div>
          <Text fw={700} size="xl" style={{ color: 'var(--aviary-ink)', fontSize: 26, lineHeight: 1.3 }}>
            {question.ask}
          </Text>
          {question.sub && (
            <Text size="sm" mt={6} style={{ color: 'var(--aviary-muted)', lineHeight: 1.45 }}>
              {question.sub}
            </Text>
          )}
        </div>
      )}

      {showEmpty ? (
        <Box
          p="lg"
          style={{
            border: '1px dashed var(--aviary-disabled)',
            borderRadius: 12,
            background: 'var(--aviary-surface)',
            textAlign: 'center',
          }}
        >
          <Stack gap="sm" align="center">
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
              No published articles yet. Write your first scenario or link an article.
            </Text>
            <UnstyledButton
              onClick={() => addOption('')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--mantine-color-sage-4)',
                color: 'var(--mantine-color-sage-7)',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <IconPlus size={14} /> Write a scenario
            </UnstyledButton>
          </Stack>
        </Box>
      ) : (
        <Stack gap="xs">
          {options.map((text, index) => {
            const selected = selectedIndex === index;
            return (
              <Box
                key={index}
                onClick={() => pick(index)}
                style={{
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: `1.5px solid ${selected ? 'var(--mantine-color-sage-6)' : 'var(--aviary-disabled)'}`,
                  background: selected ? 'var(--mantine-color-sage-0)' : '#fff',
                  cursor: 'pointer',
                  transition: 'border-color 120ms ease, background 120ms ease',
                }}
              >
                <Group wrap="nowrap" gap="sm" align="center">
                  <Radio
                    checked={selected}
                    onChange={() => pick(index)}
                    color="sage"
                    size="md"
                    style={{ pointerEvents: 'none' }}
                  />
                  <TextInput
                    value={text}
                    onChange={(e) => setOptionText(index, e.currentTarget.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && String(text || '').trim()) {
                        e.preventDefault();
                        pick(index);
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder="Describe what you help them with"
                    variant="unstyled"
                    style={{ flex: 1 }}
                    styles={{ input: { fontSize: 15, color: 'var(--aviary-ink)', padding: 0, minHeight: 24 } }}
                  />
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={(e) => { e.stopPropagation(); removeOption(index); }}
                    aria-label="Remove"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Box>
            );
          })}

          <UnstyledButton
            onClick={() => addOption('')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 4px',
              color: 'var(--mantine-color-sage-7)',
              fontSize: 13,
              fontWeight: 600,
              alignSelf: 'flex-start',
            }}
          >
            <IconPlus size={14} /> Add another
          </UnstyledButton>
        </Stack>
      )}

      <UnstyledButton
        onClick={() => navigate('/v2/articles/new')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          color: 'var(--mantine-color-sage-7)',
          fontSize: 13,
          fontWeight: 600,
          alignSelf: 'flex-start',
        }}
      >
        <IconArticle size={14} /> Write a new article or link to one
      </UnstyledButton>
    </Stack>
  );
}
